import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link as LinkIcon, Unlink, RefreshCw, Building2, CheckCircle2, AlertTriangle, Cloud, Bot, FileText, Upload, Trash2, Loader2, ShieldAlert, Download, Bell, Plus, X, Send } from "lucide-react";
import { toast } from "sonner";
import M365InboxPanel from "@/components/M365InboxPanel";

export default function IntegrationsPanel() {
  const [health, setHealth] = useState(null);
  const [org, setOrg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const h = await api.get("/xero/health");
        setHealth(h.data);
        if (h.data.connected) {
          try {
            const o = await api.get("/xero/organisation");
            setOrg(o.data);
          } catch (_) { /* token may have expired between calls */ }
        } else {
          setOrg(null);
        }
      } catch (e) {
        toast.error("Failed to load integration status");
      }
    })();
  }, [tick]);

  // Surface the message from the OAuth callback redirect (if any)
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab === "integrations") setTick((t) => t + 1);
  }, [searchParams]);

  // Listen for the callback page's postMessage so we can auto-refresh when the popup completes.
  useEffect(() => {
    const onMsg = (e) => {
      if (e?.data?.type === "xero_oauth_done") {
        setTick((t) => t + 1);
        toast.success("Xero connected");
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await api.get("/xero/auth");
      // Xero refuses to be framed (X-Frame-Options: DENY). Open in a new tab so
      // the OAuth screen renders outside any preview iframe.
      const popup = window.open(r.data.authorize_url, "_blank", "noopener,noreferrer");
      if (!popup) {
        // Pop-up blocked — fall back to escaping the iframe.
        try {
          window.top.location.href = r.data.authorize_url;
        } catch (_) {
          window.location.href = r.data.authorize_url;
        }
      } else {
        toast.info("Complete the Xero authorisation in the new tab — this page will update automatically.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Xero auth");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect Xero? Stored tokens will be removed; you'll need to reconnect to fetch fresh data.")) return;
    setBusy(true);
    try {
      await api.post("/xero/disconnect");
      toast.success("Disconnected");
      setTick((t) => t + 1);
    } catch (e) {
      toast.error("Failed to disconnect");
    } finally {
      setBusy(false);
    }
  };

  const syncPL = async () => {
    const fy = prompt("Sync P&L for which FY start year? (e.g. 2025 for FY26 = Apr 2025 → Mar 2026)", "2025");
    if (!fy) return;
    setBusy(true);
    try {
      const r = await api.post(`/xero/sync-pl/${Number(fy)}`);
      toast.success(`Synced ${r.data.months_synced} months · ${r.data.fields_updated} fields`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusy(false);
    }
  };

  if (!health) return <div className="text-sm font-mono text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="integrations-panel">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
          <Cloud className="h-3 w-3" /> Integrations
        </div>
        <h3 className="font-heading text-xl font-bold text-slate-900 mt-1">Third-party connections</h3>
        <p className="text-xs text-slate-500 mt-1">
          Authorise external services. Tokens are stored encrypted and rotated automatically.
        </p>
      </div>

      <M365InboxPanel />

      {/* Xero card */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 py-4 flex flex-row items-center justify-between">
          <CardTitle className="text-sm font-heading flex items-center gap-2">
            <span className="inline-flex items-center justify-center h-7 w-7 rounded-md bg-blue-600 text-white font-bold text-xs">X</span>
            Xero — Accounting
            {!health.configured && <Badge className="bg-rose-100 text-rose-800 border-rose-200">Not configured</Badge>}
            {health.configured && !health.connected && <Badge className="bg-amber-100 text-amber-800 border-amber-200">Disconnected</Badge>}
            {health.connected && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1"><CheckCircle2 className="h-3 w-3" /> Connected</Badge>}
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => setTick((t) => t + 1)} className="gap-1.5 text-xs">
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </Button>
        </CardHeader>
        <CardContent className="p-5 space-y-4">
          {/* Connection details */}
          {health.connected ? (
            <div className="grid sm:grid-cols-2 gap-3 text-xs">
              <Detail label="Organisation" value={health.tenant_name} icon={Building2} />
              <Detail label="Tenant ID" value={health.tenant_id} mono />
              <Detail label="Connected by" value={health.connected_by} />
              <Detail label="Token expires" value={health.expires_at ? new Date(health.expires_at).toLocaleString("en-GB") : "—"} />
              {org && (
                <>
                  <Detail label="Legal name" value={org.legal_name} />
                  <Detail label="Base currency" value={org.base_currency} mono />
                  <Detail label="Country" value={org.country_code} mono />
                  <Detail label="FY year-end" value={org.financial_year_end_month ? `${org.financial_year_end_day}/${org.financial_year_end_month}` : "—"} mono />
                </>
              )}
              <div className="sm:col-span-2 flex flex-wrap gap-1 pt-2 border-t border-slate-100">
                <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mr-2">Scopes:</span>
                {(health.scopes || []).map((s) => (
                  <Badge key={s} variant="outline" className="text-[9px] font-mono bg-slate-50 text-slate-600">{s}</Badge>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500 space-y-2">
              <p>Connect Xentra's Xero organisation so the CRM can pull invoices into the Customer Portal and populate the Budget Tracker board pack with live P&amp;L figures.</p>
              <p className="font-mono text-[11px] text-slate-400">
                Redirect URI registered with Xero must match:<br/>
                <code className="text-slate-600">{health.redirect_uri}</code>
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
            {!health.connected ? (
              <Button
                data-testid="xero-connect"
                onClick={connect}
                disabled={busy || !health.configured}
                className="gap-1.5 bg-blue-600 hover:bg-blue-700"
              >
                <LinkIcon className="h-3.5 w-3.5" /> Connect to Xero
              </Button>
            ) : (
              <>
                <Button data-testid="xero-sync-pl" onClick={syncPL} disabled={busy} className="gap-1.5 bg-blue-600 hover:bg-blue-700">
                  <RefreshCw className="h-3.5 w-3.5" /> Sync Budget Tracker (P&amp;L)
                </Button>
                <Button data-testid="xero-disconnect" onClick={disconnect} disabled={busy} variant="outline" className="gap-1.5 text-rose-700 hover:bg-rose-50">
                  <Unlink className="h-3.5 w-3.5" /> Disconnect
                </Button>
              </>
            )}
          </div>

          {/* Setup notes */}
          {!health.connected && (
            <details className="text-xs">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900">First-time setup</summary>
              <ol className="list-decimal list-inside mt-2 space-y-1 text-slate-600">
                <li>In Xero → <a className="text-blue-600 underline" href="https://developer.xero.com/app/manage" target="_blank" rel="noreferrer">developer.xero.com</a>, ensure the redirect URI above is registered on your CRM app.</li>
                <li>Click <b>Connect to Xero</b> — you'll be redirected to Xero to sign in and approve access.</li>
                <li>After approval, you'll land back here with a green "Connected" badge.</li>
                <li>Once connected, link individual customers from <b>Accounts → [Account] → Link Xero contact</b>, then their invoices appear on the Customer Portal.</li>
              </ol>
            </details>
          )}
        </CardContent>
      </Card>

      {/* Anthropic (Claude) — powers the XDR Quarterly Review generator */}
      <AnthropicIntegrationCard />

      {/* XDR Report Template — admin-uploadable .pptx */}
      <XdrTemplateCard />

      {/* Threat Advisory Template — admin-uploadable .docx or .pdf */}
      <ThreatAdvisoryTemplateCard />

      {/* Threat Advisory Alerts — email recipients that get pinged on new items */}
      <ThreatAdvisoryAlertsCard />

      {/* Future: ChartMogul / Microsoft etc. can join here */}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Anthropic (Claude) — admin pastes a sk-ant-… key once; we never show it
// back in full. "Test connection" round-trips a 1-token call to prove the
// key is live before anyone trusts the pipeline.
// ---------------------------------------------------------------------------
function AnthropicIntegrationCard() {
  const [cfg, setCfg] = React.useState(null);
  const [pendingKey, setPendingKey] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState(false);
  const [testResult, setTestResult] = React.useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/integrations/anthropic");
      setCfg(r.data);
    } catch (e) { /* non-admins get 401 — just hide */ setCfg(null); }
  };
  React.useEffect(() => { load(); }, []);

  if (!cfg) return null;

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (pendingKey) body.api_key = pendingKey.trim();
      await api.put("/admin/integrations/anthropic", body);
      toast.success("Anthropic configuration saved");
      setPendingKey("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const clearKey = async () => {
    if (!window.confirm("Clear the stored Anthropic key? The XDR review tool will stop working until a new key is pasted.")) return;
    try {
      await api.put("/admin/integrations/anthropic", { api_key: "" });
      toast.success("Key cleared");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to clear");
    }
  };

  const test = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post("/admin/integrations/anthropic/test");
      setTestResult({ ok: true, ...r.data });
      toast.success(`Connected · ${r.data.model}`);
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || "Test failed" });
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally { setTesting(false); }
  };

  return (
    <Card className="border-slate-200" data-testid="anthropic-integration">
      <CardHeader className="border-b border-slate-100 py-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <Bot className="h-4 w-4 text-blue-600" /> Anthropic (Claude)
          <span className="text-xs text-slate-400 font-normal">— powers the XDR Quarterly Review AI</span>
        </CardTitle>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${cfg.configured ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
          {cfg.configured ? "Connected" : "Not configured"}
        </span>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
          <Detail label="Current key" value={cfg.api_key_masked || "—"} mono />
          <Detail label="Default model" value={cfg.default_model} mono />
          <Detail label="Last updated" value={(cfg.updated_at || "").slice(0, 16).replace("T", " ") || "—"} />
          <Detail label="Updated by" value={cfg.updated_by || "—"} />
        </div>

        <div className="pt-2 border-t border-slate-100">
          <Label className="text-xs">Paste a new API key</Label>
          <div className="flex gap-2 mt-1.5">
            <Input
              type="password"
              placeholder="sk-ant-api03-…"
              value={pendingKey}
              onChange={(e) => setPendingKey(e.target.value)}
              data-testid="anthropic-key-input"
              autoComplete="off"
            />
            <Button onClick={save} disabled={saving || !pendingKey.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="anthropic-save-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
          <div className="text-[11px] text-slate-500 mt-1.5">
            Create one at <a className="text-blue-700 hover:underline" href="https://console.anthropic.com" target="_blank" rel="noreferrer">console.anthropic.com → API keys</a>. The key is stored on the server and never shown back in full.
          </div>
        </div>

        <div className="pt-2 border-t border-slate-100 flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={test} disabled={testing || !cfg.configured} data-testid="anthropic-test-btn">
            {testing ? "Testing…" : "Test connection"}
          </Button>
          {cfg.configured && (
            <Button variant="ghost" onClick={clearKey} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="anthropic-clear-btn">
              Clear stored key
            </Button>
          )}
          {testResult && (
            <span className={`text-xs ${testResult.ok ? "text-emerald-700" : "text-red-600"}`}>
              {testResult.ok
                ? `✓ ${testResult.model} · reply: ${testResult.reply} · ${testResult.input_tokens}+${testResult.output_tokens} tokens`
                : `✗ ${testResult.error}`}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const Detail = ({ label, value, mono = false, icon: Icon = null }) => (
  <div>
    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-0.5">{label}</div>
    <div className={`text-slate-800 flex items-center gap-1.5 ${mono ? "font-mono text-[11px]" : ""}`}>
      {Icon && <Icon className="h-3 w-3 text-slate-400" />}
      <span className="truncate">{value || "—"}</span>
    </div>
  </div>
);



// ---------------------------------------------------------------------------
// XDR Report Template — admin can upload a .pptx with `{{tokens}}` for the
// AI pipeline to fill. The uploaded file lives in object storage and is used
// by every subsequent Build Review run until the admin reverts to the
// bundled master.
// ---------------------------------------------------------------------------
function XdrTemplateCard() {
  const [cfg, setCfg] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [reverting, setReverting] = React.useState(false);
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/integrations/xdr-template");
      setCfg(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load XDR template config");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadTemplate(f);
    e.target.value = "";
  };

  const uploadTemplate = async (file) => {
    if (!file.name.toLowerCase().endsWith(".pptx")) {
      return toast.error("Please choose a .pptx file");
    }
    if (file.size > 25 * 1024 * 1024) {
      return toast.error("Template must be 25 MB or smaller");
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.put("/admin/integrations/xdr-template", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Template uploaded — new reports will use this deck");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const revert = async () => {
    if (!window.confirm("Revert to the built-in template? Your uploaded template will be removed.")) return;
    setReverting(true);
    try {
      await api.delete("/admin/integrations/xdr-template");
      toast.success("Reverted to the built-in template");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    } finally { setReverting(false); }
  };

  const bundled = cfg?.using_bundled;
  const tokens = cfg?.tokens || [];

  return (
    <Card className="border-slate-200" data-testid="xdr-template-integration">
      <CardHeader className="border-b border-slate-100 py-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <FileText className="h-4 w-4 text-blue-600" /> XDR Report Template
          <span className="text-xs text-slate-400 font-normal">— drives the AI-generated review PowerPoint</span>
        </CardTitle>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${bundled === false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
          {bundled === false ? "Custom template" : "Built-in"}
        </span>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="text-xs text-slate-600 leading-relaxed">
          Author a <span className="font-mono">.pptx</span> with mustache tokens like <code className="bg-slate-100 px-1 py-0.5 rounded">{"{{customer}}"}</code>, <code className="bg-slate-100 px-1 py-0.5 rounded">{"{{period_label}}"}</code>, <code className="bg-slate-100 px-1 py-0.5 rounded">{"{{soc_narrative}}"}</code> anywhere in the deck — the AI will fill each one from the uploaded reports (+ any additional context the engineer types) at generation time. Any slide text without a token is left untouched, so pre-formatted intro / outro slides stay as-is.
        </div>

        {!bundled && cfg && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
            <Detail label="Filename" value={cfg.name} mono icon={FileText} />
            <Detail label="Size" value={cfg.size ? `${(cfg.size / 1024).toFixed(0)} KB` : "—"} mono />
            <Detail label="Uploaded" value={cfg.updated_at ? `${(cfg.updated_at || "").slice(0,10)} · ${cfg.updated_by || ""}` : "—"} />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                 className="hidden" onChange={onPick} />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5"
            data-testid="xdr-template-upload-btn"
          >
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> {bundled ? "Upload template" : "Replace template"}</>}
          </Button>
          {!bundled && (
            <Button variant="ghost" onClick={revert} disabled={reverting}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
                    data-testid="xdr-template-revert-btn">
              {reverting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Revert to built-in
            </Button>
          )}
        </div>

        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
            Tokens discovered in the active template ({tokens.length})
          </div>
          {tokens.length === 0 ? (
            <div className="text-xs text-slate-400">No <code>{"{{tokens}}"}</code> found — upload a template with mustache placeholders to let AI fill them in.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5" data-testid="xdr-template-tokens">
              {tokens.map((t) => (
                <code key={t} className="text-[11px] font-mono bg-slate-100 text-slate-700 border border-slate-200 rounded px-1.5 py-0.5">
                  {`{{${t}}}`}
                </code>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ---------------------------------------------------------------------------
// Threat Advisory Template — admin uploads a .docx (preferred, keeps logo /
// branding / fonts) or .pdf with mustache tokens like {{executive_summary}}.
// The "Prepare Threat Advisory" button on the Threat Advisories detail
// dialog uses this template + Claude's enrichment to produce the customer
// PDF. Mirrors the XDR template card pattern directly above.
// ---------------------------------------------------------------------------
function ThreatAdvisoryTemplateCard() {
  const [cfg, setCfg] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const [reverting, setReverting] = React.useState(false);
  const fileRef = React.useRef(null);

  const load = React.useCallback(async () => {
    try {
      const r = await api.get("/admin/integrations/threat-advisory-template");
      setCfg(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load Threat Advisory template config");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    uploadTemplate(f);
    e.target.value = "";
  };

  const uploadTemplate = async (file) => {
    const lower = file.name.toLowerCase();
    if (!lower.endsWith(".docx") && !lower.endsWith(".pdf")) {
      return toast.error("Please choose a .docx or .pdf file");
    }
    if (file.size > 25 * 1024 * 1024) {
      return toast.error("Template must be 25 MB or smaller");
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.put("/admin/integrations/threat-advisory-template", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Template uploaded — new advisories will use this layout");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const revert = async () => {
    if (!window.confirm("Remove the uploaded Threat Advisory template? Engineers won't be able to prepare advisories until you upload another one.")) return;
    setReverting(true);
    try {
      await api.delete("/admin/integrations/threat-advisory-template");
      toast.success("Template removed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally { setReverting(false); }
  };

  const download = async () => {
    try {
      const r = await api.get("/admin/integrations/threat-advisory-template/download", { responseType: "blob" });
      const blob = new Blob([r.data], { type: cfg?.ext === ".pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = cfg?.name || `threat_advisory_template${cfg?.ext || ".docx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const configured = cfg?.configured;
  const inUse = cfg?.tokens_in_use || [];
  const unused = cfg?.tokens_unused || [];
  const available = cfg?.available_tokens || [];

  return (
    <Card className="border-slate-200" data-testid="threat-advisory-template-integration">
      <CardHeader className="border-b border-slate-100 py-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-heading flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-rose-600" /> Threat Advisory Template
          <span className="text-xs text-slate-400 font-normal">— drives the customer-facing PDF for Threat Advisories</span>
        </CardTitle>
        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${configured ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
          {configured ? "Uploaded" : "Not uploaded"}
        </span>
      </CardHeader>
      <CardContent className="p-5 space-y-4">
        <div className="text-xs text-slate-600 leading-relaxed">
          Author a <span className="font-mono">.docx</span> (recommended — keeps your logo, fonts and colours) or a
          <span className="font-mono"> .pdf</span> with mustache tokens anywhere in the body — AI will fill each one from
          the alert plus its AI enrichment at generation time. Engineers can edit the filled copy before download or send.
        </div>

        {configured && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
            <Detail label="Filename" value={cfg.name} mono icon={FileText} />
            <Detail label="Type" value={cfg.ext} mono />
            <Detail label="Size" value={cfg.size ? `${(cfg.size / 1024).toFixed(0)} KB` : "—"} mono />
            <Detail label="Uploaded" value={cfg.updated_at ? `${(cfg.updated_at || "").slice(0,10)} · ${cfg.updated_by || ""}` : "—"} />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".docx,.pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf"
                 className="hidden" onChange={onPick} />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="bg-rose-600 hover:bg-rose-700 gap-1.5"
            data-testid="threat-advisory-template-upload-btn"
          >
            {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4" /> {configured ? "Replace template" : "Upload template"}</>}
          </Button>
          {configured && (
            <>
              <Button variant="outline" onClick={download} className="gap-1.5"
                      data-testid="threat-advisory-template-download-btn">
                <Download className="h-4 w-4" /> Download current
              </Button>
              <Button variant="ghost" onClick={revert} disabled={reverting}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
                      data-testid="threat-advisory-template-revert-btn">
                {reverting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Remove
              </Button>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Tokens detected in the uploaded template */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              Tokens found in your template ({inUse.length})
            </div>
            {!configured ? (
              <div className="text-xs text-slate-400">Upload a template to scan it.</div>
            ) : inUse.length === 0 ? (
              <div className="text-xs text-amber-700">No supported <code>{"{{tokens}}"}</code> found — the generated advisory won't have anywhere to put the AI content.</div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="threat-advisory-template-tokens">
                {inUse.map((t) => (
                  <code key={t} className="text-[11px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-1.5 py-0.5">
                    {`{{${t}}}`}
                  </code>
                ))}
              </div>
            )}
          </div>
          {/* Tokens the system supports but the template isn't using yet */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
              All supported tokens ({available.length})
            </div>
            <div className="flex flex-wrap gap-1.5">
              {available.map((t) => {
                const used = inUse.includes(t);
                return (
                  <code key={t} className={`text-[11px] font-mono rounded px-1.5 py-0.5 border ${used ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-50 text-slate-500 border-slate-200"}`}>
                    {`{{${t}}}`}
                  </code>
                );
              })}
            </div>
            {configured && unused.length > 0 && (
              <div className="text-[11px] text-slate-500 mt-2">
                Grey tokens aren't in your current template — add them anywhere in the body to include that section in the advisory.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}



// FastAPI returns 422 details as an array of {loc, msg, type} dicts.
// Passing that array straight into toast.error → React tries to render an
// object as a child, which crashes the whole tree with the CRA error
// overlay — exactly the "page opens but never loads" bug a user hit on
// the Threat Advisory Alerts toggle. Normalise anything we show as text.
const _asErrorMessage = (detail, fallback = "Something went wrong") => {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => {
        if (!d) return "";
        if (typeof d === "string") return d;
        const loc = Array.isArray(d.loc) ? d.loc.join(".") : "";
        return [loc, d.msg].filter(Boolean).join(": ") || JSON.stringify(d);
      })
      .filter(Boolean)
      .join("; ") || fallback;
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
};

// ---------------------------------------------------------------------------
// Threat Advisory Alerts — admin configures the email addresses that get
// pinged whenever a new advisory lands in the queue.
// ---------------------------------------------------------------------------
const SEVERITY_OPTIONS = ["Any", "Informational", "Low", "Medium", "High", "Critical"];

function ThreatAdvisoryAlertsCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [newRecipient, setNewRecipient] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/threat-advisories/alerts/settings");
      setCfg({
        enabled: !!r.data?.enabled,
        recipients: r.data?.recipients || [],
        min_severity: r.data?.min_severity || "Low",
        updated_at: r.data?.updated_at,
        updated_by: r.data?.updated_by,
      });
    } catch (e) {
      toast.error(_asErrorMessage(e?.response?.data?.detail, "Could not load alert settings"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    const body = {
      enabled: next.enabled ?? cfg.enabled,
      recipients: next.recipients ?? cfg.recipients,
      min_severity: next.min_severity ?? cfg.min_severity,
    };
    setSaving(true);
    try {
      const r = await api.put("/threat-advisories/alerts/settings", body);
      setCfg({ ...cfg, ...r.data });
      toast.success("Alert settings saved");
    } catch (e) {
      toast.error(_asErrorMessage(e?.response?.data?.detail, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const addRecipient = () => {
    const em = (newRecipient || "").trim();
    if (!em) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
      toast.error("That doesn't look like a valid email address");
      return;
    }
    if (cfg.recipients.some((r) => r.toLowerCase() === em.toLowerCase())) {
      toast.info("That address is already on the list");
      setNewRecipient("");
      return;
    }
    save({ recipients: [...cfg.recipients, em] });
    setNewRecipient("");
  };

  const removeRecipient = (em) => {
    save({ recipients: cfg.recipients.filter((r) => r !== em) });
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/threat-advisories/alerts/test");
      toast.success(`Test alert dispatched to ${(r.data?.sent_to || []).join(", ")}`);
    } catch (e) {
      toast.error(_asErrorMessage(e?.response?.data?.detail, "Test send failed"));
    } finally { setTesting(false); }
  };

  if (loading || !cfg) {
    return (
      <Card className="border-slate-200" data-testid="threat-advisory-alerts-integration">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Bell className="h-4 w-4 text-rose-600" /> Threat Advisory Alerts
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-slate-500 py-3">
          <Loader2 className="h-3 w-3 inline-block mr-1 animate-spin" /> Loading…
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="threat-advisory-alerts-integration">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2 justify-between">
          <span className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-rose-600" /> Threat Advisory Alerts
          </span>
          {cfg.enabled && cfg.recipients.length > 0
            ? <Badge data-testid="threat-alerts-status-badge" className="bg-emerald-100 text-emerald-800 border-emerald-200">Active</Badge>
            : cfg.enabled
              ? <Badge data-testid="threat-alerts-status-badge" className="bg-amber-100 text-amber-800 border-amber-200">Enabled — add a recipient</Badge>
              : <Badge data-testid="threat-alerts-status-badge" className="bg-slate-100 text-slate-600 border-slate-200">Off</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 pt-0">
        <p className="text-[12px] text-slate-600 leading-relaxed">
          Send an email to these people whenever a new threat advisory lands in
          the queue. AI auto-assigns a severity before the email is sent so
          reviewers can decide quickly whether to issue a customer advisory.
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" type="button" variant={cfg.enabled ? "default" : "outline"}
                  onClick={(e) => { e.preventDefault(); save({ enabled: !cfg.enabled }); }}
                  disabled={saving}
                  data-testid="threat-alerts-toggle">
            {cfg.enabled ? "Enabled — click to disable" : "Disabled — click to enable"}
          </Button>
          <div className="flex items-center gap-1 text-xs text-slate-600">
            <Label className="text-xs">Alert on</Label>
            <select
              className="border border-slate-300 rounded px-2 py-1 text-xs"
              value={cfg.min_severity}
              onChange={(e) => save({ min_severity: e.target.value })}
              data-testid="threat-alerts-min-severity"
              disabled={saving}
            >
              {SEVERITY_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s === "Any" ? "Any — every new advisory" : `${s} and above`}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <Label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">
            Recipients
          </Label>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {cfg.recipients.length === 0 && (
              <span className="text-xs text-slate-400 italic">No recipients yet — add one below.</span>
            )}
            {cfg.recipients.map((em) => (
              <span key={em}
                    className="inline-flex items-center gap-1 bg-rose-50 border border-rose-200 text-rose-800 rounded-full px-2 py-0.5 text-xs"
                    data-testid={`threat-alerts-recipient-${em}`}>
                {em}
                <button type="button" onClick={() => removeRecipient(em)}
                        className="hover:text-rose-900"
                        aria-label={`Remove ${em}`}
                        data-testid={`threat-alerts-remove-${em}`}>
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Input
              type="email"
              value={newRecipient}
              onChange={(e) => setNewRecipient(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }}
              placeholder="soc@xentra.co.uk"
              className="h-8 text-xs"
              data-testid="threat-alerts-new-recipient"
            />
            <Button size="sm" type="button" variant="outline" onClick={addRecipient}
                    disabled={saving || !newRecipient}
                    data-testid="threat-alerts-add-btn">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between pt-1 border-t border-slate-100">
          <div className="text-[11px] text-slate-500">
            {cfg.updated_at
              ? <>Last updated {new Date(cfg.updated_at).toLocaleString()}{cfg.updated_by ? ` by ${cfg.updated_by}` : ""}</>
              : "Not configured yet"}
          </div>
          <Button size="sm" type="button" variant="outline"
                  onClick={sendTest}
                  disabled={testing || !cfg.enabled || cfg.recipients.length === 0}
                  data-testid="threat-alerts-test-btn">
            {testing
              ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Sending…</>
              : <><Send className="h-3 w-3 mr-1" /> Send test</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
